import { useCallback, useMemo } from "react";

import Fuse from "fuse.js";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { graphql } from "relay-runtime";

import {
    useSearchSuggestionsQuery,
    useSearchSuggestionsQuery$data
} from "@/__generated__/useSearchSuggestionsQuery.graphql";

import { CategorySearchSuggestion } from "../components/template-category-search-results-relay";
import { TemplateSearchSuggestion } from "../components/template-fuzzy-search-results-relay";

import { useQueryLocale } from "./use-query-locale";

export type SearchSuggestion =
    | CategorySearchSuggestion
    | TemplateSearchSuggestion;

export const FUSE_OPTIONS = {
    keys: [
        { name: "name", weight: 0.8 }, // Increased weight for better matches on name
        { name: "description", weight: 0.5 },
        { name: "contributor", weight: 0.3 },
        { name: "contributorOrganization", weight: 0.3 },
        { name: "customTag", weight: 0.3 }
    ],
    includeScore: true,
    threshold: 0.4
};

const FUSE_RESULT_LIMIT = 35;

const SLUG_TO_TAGS: Record<string, string[]> = {
    "project-plan": ["proposal", "gantt", "timeline"],
    "master-project-documentation": ["proposal"],
    "annual-plan-one-pager": ["proposal"],
    "mutual-action-plan": ["proposal"],
    "executive-business-review": ["proposal"],
    "devops-runbook": ["gantt", "timeline"],
    "software-architecture-review": ["gantt"],
    "problem-framing": ["faq"],
    "safe-lean-business-case": ["faq"],
    "hr-knowledge-base": ["faq"],
    "design-sprint": ["timeline"],
    "project-kickoff": ["timeline"],
    "year-in-review": ["timeline"]
};

export const WHITEBOARD_TYPE_ID = "1HlskYfRNciSnQ02SDhCPs";

const getAuthor = (
    template: useSearchSuggestionsQuery$data["templates"]["edges"][0]["node"]
) => {
    return template.contributor?.__typename === "ContentPlatformAuthor"
        ? template.contributor
        : null;
};

const getOrganization = (
    template: useSearchSuggestionsQuery$data["templates"]["edges"][0]["node"]
) => {
    return template.contributor?.__typename === "ContentPlatformOrganization"
        ? template.contributor
        : template.contributor?.__typename === "ContentPlatformAuthor"
        ? template.contributor?.organization
        : null;
};

const addCustomTagToTemplatesBasedOnSearchTerm = (
    template: useSearchSuggestionsQuery$data["templates"]["edges"][0]["node"]
) => {
    const tags = (template.urlSlug ? SLUG_TO_TAGS[template.urlSlug] : []) ?? [];

    /**
     * Whiteboard templates for custom tag search term "whiteboard templates"
     */
    if (template.templateType?.some((type) => type.id === WHITEBOARD_TYPE_ID)) {
        tags.push("whiteboard templates");
    }

    return tags;
};

export const parseTemplateSearchItems = (
    templates: useSearchSuggestionsQuery$data["templates"]
) => {
    const items: TemplateSearchSuggestion[] = [];
    for (const { node: template } of templates.edges) {
        if (template.urlSlug) {
            items.push({
                id: template.urlSlug,
                name: template.name,
                description: template.previewBlurb,
                contributor: getAuthor(template)?.name,
                contributorOrganization: getOrganization(template)?.name,
                customTag: addCustomTagToTemplatesBasedOnSearchTerm(template),

                type: "template",
                templateType: template.templateType?.[0]?.templateTypeName
            });
        }
    }
    return items;
};

export const parseCategorySearchItems = (
    categories: useSearchSuggestionsQuery$data["categories"]
) => {
    const items: CategorySearchSuggestion[] = [];
    for (const { node } of categories.edges) {
        if (
            node.context.urlSlug !== "featured" &&
            node.context.urlSlug !== "recommended" &&
            node.context.urlSlug !== "project-planning" &&
            node.context.urlSlug !== "business-strategy" &&
            node.context.urlSlug !== "productivity" &&
            node.context.urlSlug !== "docs-and-reports" &&
            node.context.urlSlug !== "startup" &&
            node.context.urlSlug !== "personal"
        ) {
            items.push({
                id: node.context.urlSlug,
                name: node.context.name,
                description: node.context.shortDescriptionOneLiner,
                type: "category"
            });
        }
    }
    return items;
};

export const UseSearchSuggestionsQuery = graphql`
    query useSearchSuggestionsQuery($locale: String!) {
        templates(
            search: { queries: [{ first: 200, withLocales: [$locale] }] }
        ) {
            edges {
                node {
                    urlSlug
                    name
                    previewBlurb
                    templateType {
                        id
                        templateTypeName
                    }
                    contributor {
                        __typename
                        ... on ContentPlatformOrganization {
                            name
                        }
                        ... on ContentPlatformAuthor {
                            name
                            organization {
                                name
                            }
                        }
                    }
                }
            }
        }
        categories: contentFacet(
            first: 25
            forFields: ["category"]
            forContentType: "Template"
            withLocales: [$locale]
        ) {
            edges {
                node {
                    context
                }
            }
        }
    }
`;

export const useSearchSuggestionsLoader = () => {
    const locale = useQueryLocale();
    const [queryRef, loadQuery] = useQueryLoader<useSearchSuggestionsQuery>(
        UseSearchSuggestionsQuery
    );
    const loadSearchSuggestionsQuery = useCallback(() => {
        loadQuery({ locale });
    }, [loadQuery, locale]);
    return [queryRef, loadSearchSuggestionsQuery] as const;
};

export const useSearchSuggestions = (
    queryRef: PreloadedQuery<useSearchSuggestionsQuery>,
    inputValue?: string | SearchSuggestion | null
) => {
    const { categories, templates } =
        usePreloadedQuery<useSearchSuggestionsQuery>(
            UseSearchSuggestionsQuery,
            queryRef
        );

    const fuse = useMemo(
        () =>
            new Fuse(
                [
                    ...parseTemplateSearchItems(templates),
                    ...parseCategorySearchItems(categories)
                ],
                FUSE_OPTIONS
            ),
        [templates, categories]
    );

    return useMemo(() => {
        let results: SearchSuggestion[] = [];

        if (inputValue) {
            results = fuse
                .search(
                    typeof inputValue === "string"
                        ? inputValue
                        : inputValue?.name ?? ""
                )
                .sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity)) // Sort by score in ascending order
                .slice(0, FUSE_RESULT_LIMIT) // Get the top results
                .map((result) => result.item);
        }

        return results;
    }, [fuse, inputValue]);
};
